import { memo } from "react";
import { Flex, Image, Text } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { useTranslate } from "@tolgee/react";
import { colors } from "../../../constants/GlobalStyles";
import { AppTooltip } from "../../../components/AppTooltip";
import { TutorNavigationIcon } from "./TutorNavigationIcon";
import type { tutorRouteTarget } from "../integration/tutorLocation";

export type TutorNavigationTarget = ReturnType<typeof tutorRouteTarget>;
export type TutorNavigationCallAction =
  | { state: "unavailable" }
  | { state: "busy" }
  | { state: "ready"; onActivate: () => void };

export interface TutorSidebarEntryConfiguration {
  tutorPortraitUrl: string;
  bodyTarget: TutorNavigationTarget;
  chatTarget: TutorNavigationTarget;
  callAction: TutorNavigationCallAction;
}
export interface TutorSidebarEntryProps extends TutorSidebarEntryConfiguration {
  isCollapsed: boolean;
  isCurrent: boolean;
}

const focusStyle = { outline: `2px solid ${colors.primaryAction.default}`, outlineOffset: "2px" };

/** Presentation only. The host resolves authorized routes and owns the one call. */
export const TutorSidebarEntry = memo(function TutorSidebarEntry({
  tutorPortraitUrl, bodyTarget, chatTarget, callAction, isCollapsed, isCurrent,
}: TutorSidebarEntryProps) {
  const { t } = useTranslate();
  const tutorLabel = t("tutor.title", "tutor");
  if (isCollapsed) return <AppTooltip label={tutorLabel} placement="right" openDelay={200}><Flex as={Link} to={bodyTarget} state={bodyTarget.state} aria-label={tutorLabel}
    aria-current={isCurrent ? "page" : undefined} mt="10px" minH="44px" justify="center" align="center"
    borderRadius="16px" _hover={{ bg: colors.surface.primary }} _focusVisible={focusStyle}>
    <Image src={tutorPortraitUrl} alt="" boxSize="36px" objectFit="contain" />
  </Flex></AppTooltip>;

  const callHint = callAction.state === "unavailable" ? t("tutor.calls_unavailable", "calls unavailable right now") :
    callAction.state === "busy" ? t("voice.entry.starting", "starting call…") : t("voice.entry.callMonkey", "call monkey tutor");
  return <Flex data-testid="tutor-sidebar-entry" align="center" gap="2px" mt="10px" p="7px" borderRadius="16px"
    bg={colors.surface.white} border={`1px solid ${colors.surface.border}`} boxShadow="0 1px 2px rgba(40,60,50,.04)"
    sx={{ "&:has([data-tutor-card-body]:hover), &:has([data-tutor-card-body]:focus-visible)": { background: colors.surface.primary } }}>
    <Flex as={Link} data-tutor-card-body to={bodyTarget} state={bodyTarget.state} aria-label={tutorLabel}
      aria-current={isCurrent ? "page" : undefined} align="center" gap="9px" flex={1} minW={0} marginInlineEnd="7px"
      borderRadius="10px" _hover={{ textDecoration: "none" }} _focusVisible={focusStyle}>
      <Image src={tutorPortraitUrl} alt="" boxSize="36px" flexShrink={0} objectFit="contain" />
      <Text fontSize="14px" fontWeight={500} color={colors.text.header} noOfLines={1}>{t("monkey", "monkey")}</Text>
    </Flex>
    <AppTooltip label={t("chat", "chat")} openDelay={200}>
      <Flex as={Link} to={chatTarget} state={chatTarget.state} aria-label={t("chat", "chat")} boxSize="32px" flexShrink={0}
        align="center" justify="center" borderRadius="50%" color={colors.text.body}
        _hover={{ bg: colors.surface.secondary }} _focusVisible={focusStyle}>
        <TutorNavigationIcon name="chat" />
      </Flex>
    </AppTooltip>
    <AppTooltip label={callHint} openDelay={200}>
      <Flex as="button" type="button" aria-label={t("voice.entry.callMonkey", "call monkey tutor")}
        aria-disabled={callAction.state !== "ready"} aria-busy={callAction.state === "busy" || undefined}
        onClick={() => { if (callAction.state === "ready") callAction.onActivate(); }}
        align="center" gap="6px" h="32px" flexShrink={0} paddingInlineStart="10px" paddingInlineEnd="12px"
        borderRadius="99px" bg={colors.primaryAction.light2} color={colors.primaryAction.hover} fontSize="13.5px" fontWeight={500}
        cursor={callAction.state === "ready" ? "pointer" : "default"}
        _hover={{ bg: callAction.state === "ready" ? colors.primaryAction.selectedLight : undefined }} _focusVisible={focusStyle}>
        <TutorNavigationIcon name="phone" />{t("tutor.navigation.call", "call")}
      </Flex>
    </AppTooltip>
  </Flex>;
});
